import React, {useState} from "react";
import {Box, Button, TextField, Typography} from "@mui/material";
import AuthShell, {BLUE, ErrorText, MUTED} from "../components/AuthShell";
import {sendResetCode, setNewPassword, verifyResetCode} from "../util/api";

// Supplier portal password reset: email a code, check it, set a new password.
//
// Step 1 ALWAYS advances, whether or not the address exists. Telling the user "no such account" here
// would hand out exactly the directory the sign-in form refuses to give -- so an unknown address gets
// the same screen and simply never receives a code.
//
// An INVITATION enters at the last step. The emailed link carries the same key and code this flow
// would otherwise collect, and following it is itself the proof of address, so there is nothing left
// to establish -- the contact only has to choose a password. Same endpoint, same single-use key.

type Phase = "email" | "code" | "password" | "done";

const MINIMUM_PASSWORD_LENGTH = 8;

export type Invitation = {key: string; code: string};

export default function ForgotPassword({onBack, invitation}: {onBack: () => void; invitation?: Invitation}) {
    const [phase, setPhase] = useState<Phase>(invitation ? "password" : "email");
    const [email, setEmail] = useState("");
    const [key, setKey] = useState(invitation?.key ?? "");
    const [code, setCode] = useState(invitation?.code ?? "");
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);

    const submitEmail = async (event: React.FormEvent) => {
        event.preventDefault();
        if (busy) return;
        setBusy(true);
        setError("");

        const result = await sendResetCode(email.trim());

        setBusy(false);

        if (result.status !== 200 || !result.data?.Key) {
            setError("Something went wrong. Please try again.");
            return;
        }

        setKey(result.data.Key);
        setPhase("code");
    };

    const submitCode = async (event: React.FormEvent) => {
        event.preventDefault();
        if (busy) return;
        setBusy(true);
        setError("");

        const result = await verifyResetCode(key, code.trim());

        setBusy(false);

        if (result.status === 429) {
            setError("Too many attempts. Request a new code.");
            return;
        }

        if (result.status !== 200) {
            setError("That code is not right, or it has expired.");
            return;
        }

        setPhase("password");
    };

    const submitPassword = async (event: React.FormEvent) => {
        event.preventDefault();
        if (busy) return;

        if (password !== confirm) {
            setError("Those passwords do not match.");
            return;
        }

        if (password.length < MINIMUM_PASSWORD_LENGTH) {
            setError(`Use at least ${MINIMUM_PASSWORD_LENGTH} characters.`);
            return;
        }

        if (password.includes(" ")) {
            setError("Passwords cannot contain spaces.");
            return;
        }

        setBusy(true);
        setError("");

        const result = await setNewPassword(key, code.trim(), password);

        setBusy(false);

        if (result.status !== 200) {
            setError(invitation
                ? "That invitation is no longer valid. Ask your contact to send a new one."
                : "That reset link is no longer valid. Please start again.");
            return;
        }

        setPhase("done");
    };

    const backLink = (
        <Typography onClick={onBack} sx={{fontSize: 13, color: BLUE, cursor: "pointer", textAlign: "center"}}>
            Back to sign in
        </Typography>
    );

    if (phase === "done") {
        return (
            <AuthShell title="Password updated" subtitle="You can now sign in with your new password.">
                {backLink}
            </AuthShell>
        );
    }

    if (phase === "password") {
        return (
            <AuthShell title={invitation ? "Choose a password" : "Choose a new password"}
                       subtitle={invitation ? "Set a password to finish setting up your account." : undefined}>
                <form onSubmit={submitPassword}>
                    <Box sx={{display: "flex", flexDirection: "column", gap: 2}}>
                        <TextField label={invitation ? "Password" : "New password"} type="password" size="small" autoComplete="new-password"
                                   value={password} onChange={e => setPassword(e.target.value)} required fullWidth/>
                        <TextField label={invitation ? "Confirm password" : "Confirm new password"} type="password" size="small" autoComplete="new-password"
                                   value={confirm} onChange={e => setConfirm(e.target.value)} required fullWidth/>

                        {error && <ErrorText>{error}</ErrorText>}

                        <Button type="submit" variant="contained" disableElevation disabled={busy}
                                sx={{backgroundColor: BLUE, textTransform: "none", "&:hover": {backgroundColor: "#1180B8"}}}>
                            {busy ? "Saving\u2026" : "Save password"}
                        </Button>
                    </Box>
                </form>
                {backLink}
            </AuthShell>
        );
    }

    if (phase === "code") {
        return (
            <AuthShell title="Check your email"
                       subtitle="If that address is registered, we have sent it a six-digit code. It expires in 15 minutes.">
                <form onSubmit={submitCode}>
                    <Box sx={{display: "flex", flexDirection: "column", gap: 2}}>
                        <TextField label="Code" size="small"
                                   slotProps={{htmlInput: {inputMode: "numeric", maxLength: 6}}}
                                   value={code} onChange={e => setCode(e.target.value)} required fullWidth/>

                        {error && <ErrorText>{error}</ErrorText>}

                        <Button type="submit" variant="contained" disableElevation disabled={busy}
                                sx={{backgroundColor: BLUE, textTransform: "none", "&:hover": {backgroundColor: "#1180B8"}}}>
                            {busy ? "Checking\u2026" : "Continue"}
                        </Button>
                    </Box>
                </form>
                {backLink}
            </AuthShell>
        );
    }

    return (
        <AuthShell title="Forgot password"
                   subtitle="Enter your email address and we will send you a code to reset your password.">
            <form onSubmit={submitEmail}>
                <Box sx={{display: "flex", flexDirection: "column", gap: 2}}>
                    <TextField label="Email" type="email" size="small" autoComplete="username"
                               value={email} onChange={e => setEmail(e.target.value)} required fullWidth/>

                    {error && <ErrorText>{error}</ErrorText>}

                    <Button type="submit" variant="contained" disableElevation disabled={busy}
                            sx={{backgroundColor: BLUE, textTransform: "none", "&:hover": {backgroundColor: "#1180B8"}}}>
                        {busy ? "Sending\u2026" : "Send code"}
                    </Button>
                </Box>
            </form>
            {backLink}
            <Typography sx={{fontSize: 12, color: MUTED, textAlign: "center"}}>
                We never confirm whether an address is registered.
            </Typography>
        </AuthShell>
    );
}
